import React from "react";

import ChevronDownIcon from "@uui/ChevronDown";
import CopyIcon from "@uui/Copy01";
import { isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import IconHeading from "@app/analytics/components/IconHeading";
import { ICON_BY_CATEGORY } from "@app/analytics/components/Icons";
import Contributors from "@app/analytics/Pulse/components/Contributors";
import SessionListPopover from "@app/analytics/Pulse/components/SessionListPopover";
import type { PulseTopic } from "@app/analytics/Pulse/types";
import { getPulseTopicCopyContent } from "@app/analytics/Pulse/utils";
import { ELLIPSIS_SX } from "@app/common/styles";
import { pluralizePhrase, splitParagraphs } from "@app/common/utils";

import CopyButton from "@rui/CopyButton";
import { Column, Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import ActionItems from "./ActionItems";
import KeyQuestions from "./KeyQuestions";
import {
  Bullet,
  ShareOfMeetings,
  SummaryRow,
  TableRow,
  TableRowExpansion,
  TableRowExpansionShim,
  TableRowSummary,
  TableSubHeading,
  Text,
} from "./styles";
import TopicsTableClips from "./TopicsTableClips";
import { Action, TextWithIcon } from "../styles";

interface TopicsTableRowProps {
  expanded: boolean;
  onToggleExpanded: (topicId: PulseTopic["id"]) => void;
  topic: PulseTopic;
}

const getFormattedDateRange = ({
  startDate,
  endDate,
}: PulseTopic["dateRange"]) => {
  const formattedStartDate = startDate.toFormat("MMM d, yyyy");
  const formattedEndDate = endDate.toFormat("MMM d, yyyy");

  // if the time range only spans a single day, just show one date
  if (formattedStartDate === formattedEndDate) return formattedStartDate;

  if (startDate.year !== endDate.year) {
    return `${formattedStartDate}-${formattedEndDate}`;
  }

  if (startDate.month !== endDate.month) {
    return `${startDate.toFormat("MMM d")}-${endDate.toFormat("MMM d, yyyy")}`;
  }

  return `${startDate.toFormat("MMM d")}-${endDate.toFormat("d, yyyy")}`;
};

const getTooltipTitle = (topic: PulseTopic) =>
  `${pluralizePhrase(topic.clips.length, "Clip")}, ${pluralizePhrase(
    topic.actionItems.length,
    "Action Item"
  )}, and ${pluralizePhrase(topic.keyQuestions.length, "Key Question")}`;

const TopicsTableRowExpansion = ({
  expanded,
  topic,
}: {
  expanded: boolean;
  topic: PulseTopic;
}) => {
  const summaryParagraphs = splitParagraphs(topic.summary);
  const hasClips = !isEmpty(topic.clips);

  return (
    <TableRowExpansion>
      <Column spacing={2.5}>
        <Column spacing={1}>
          <Column sx={{ px: 3, pb: hasClips ? 1.5 : 0 }} spacing={1}>
            <TableSubHeading>
              <IconHeading type="summary" shape="square" size="small">
                Summary
              </IconHeading>
            </TableSubHeading>
            <Column spacing={1}>
              {isEmpty(summaryParagraphs) ? (
                <Text light>No summary</Text>
              ) : (
                summaryParagraphs.map((p) => <Text key={p}>{p}</Text>)
              )}
            </Column>
          </Column>
          {expanded && hasClips && <TopicsTableClips clips={topic.clips} />}
        </Column>
        <ActionItems actionItems={topic.actionItems} />
        <KeyQuestions keyQuestions={topic.keyQuestions} />
      </Column>
      <TableRowExpansionShim />
    </TableRowExpansion>
  );
};

const TopicsTableRow = ({
  expanded,
  onToggleExpanded,
  topic,
}: TopicsTableRowProps) => {
  const tooltipTitle = getTooltipTitle(topic);

  return (
    <TableRow
      disableGutters
      elevation={0}
      expanded={expanded}
      TransitionProps={{ unmountOnExit: true }}
    >
      <TableRowSummary onClick={() => onToggleExpanded(topic.id)}>
        <Row spacing={3} variant="apart" sx={{ width: "100%" }}>
          <Row variant="left" spacing={1} sx={{ overflow: "hidden" }}>
            <Action
              className="topics-table__expand-icon"
              variant="text"
              fontSize="20px"
            >
              <ChevronDownIcon />
            </Action>
            <Column sx={{ overflow: "hidden", flexShrink: 1 }} spacing={0.5}>
              <Typography
                variant="bodyPrimaryMed"
                sx={{
                  color: "shades.neutral.800",
                  lineHeight: 20 / 16,
                  ...(!expanded && ELLIPSIS_SX),
                }}
                title={topic.title}
              >
                {topic.title}
              </Typography>
              <SummaryRow spacing={1} variant="left">
                <TooltipWrapper title={tooltipTitle}>
                  <Row spacing={1.5}>
                    <TextWithIcon
                      Icon={ICON_BY_CATEGORY.clips.Icon}
                      iconColor="#80B22D"
                    >
                      {topic.clips.length}
                    </TextWithIcon>
                    <TextWithIcon
                      Icon={ICON_BY_CATEGORY.action_items.Icon}
                      iconColor="#706CD6"
                    >
                      {topic.actionItems.length}
                    </TextWithIcon>
                    <TextWithIcon
                      Icon={ICON_BY_CATEGORY.key_questions.Icon}
                      iconColor="#D98C4C"
                    >
                      {topic.keyQuestions.length}
                    </TextWithIcon>
                  </Row>
                </TooltipWrapper>
                <Row variant="left" spacing={1} className="hide-md">
                  <Bullet />
                  <SessionListPopover sessions={topic.sessions}>
                    <Typography variant="labelMed" color="shades.neutral.500">
                      From {pluralizePhrase(topic.numMeetings, "meeting")} (
                      {getFormattedDateRange(topic.dateRange)})
                    </Typography>
                  </SessionListPopover>
                </Row>
              </SummaryRow>
            </Column>
          </Row>
          <Row spacing={3} variant="right">
            <ShareOfMeetings value={topic.shareOfMeetings} />
            <Contributors
              contributors={topic.contributors}
              maxVisible={3}
              sx={{ minWidth: "110px" }}
            />
            <Row spacing={1} variant="right">
              <CopyButton
                copyValue={getPulseTopicCopyContent(topic)}
                renderIconButton={({ children, ...props }) => (
                  <Action
                    {...props}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onClick?.(e);
                    }}
                  >
                    {children ?? <CopyIcon />}
                  </Action>
                )}
              />
            </Row>
          </Row>
        </Row>
      </TableRowSummary>
      <TopicsTableRowExpansion expanded={expanded} topic={topic} />
    </TableRow>
  );
};

export default TopicsTableRow;
